import React, { useState } from 'react';
import { 
  Building2, 
  Clock, 
  Send, 
  CheckCircle2, 
  AlertCircle, 
  Store, 
  Coins, 
  ShieldCheck, 
  XCircle,
  Phone,
  FileText,
  HelpCircle
} from 'lucide-react';
import { CashbackMerchantAccessRequest } from './CashbackTypes';

interface CashbackMerchantRequestViewProps {
  currentUser?: any;
  existingRequest?: CashbackMerchantAccessRequest;
  onSubmitRequest: (data: {
    company_name: string;
    company_category: string;
    company_city: string;
    cnpj_cpf: string;
    user_phone: string;
    notes: string;
  }) => void;
}

export const CashbackMerchantRequestView: React.FC<CashbackMerchantRequestViewProps> = ({
  currentUser,
  existingRequest,
  onSubmitRequest
}) => {
  const [companyName, setCompanyName] = useState('');
  const [companyCategory, setCompanyCategory] = useState('Alimentação & Gastronomia');
  const [companyCity, setCompanyCity] = useState('Ribeirão Preto - SP');
  const [cnpjCpf, setCnpjCpf] = useState('');
  const [userPhone, setUserPhone] = useState(currentUser?.phone || '');
  const [notes, setNotes] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName) return;

    onSubmitRequest({
      company_name: companyName,
      company_category: companyCategory,
      company_city: companyCity,
      cnpj_cpf: cnpjCpf,
      user_phone: userPhone,
      notes: notes
    });

    setIsSubmitted(true);
  };

  const req = existingRequest || (isSubmitted ? { status: 'pending' } as any : null);

  return (
    <div className="w-full space-y-6">
      {/* HEADER BANNER */}
      <div className="bg-gradient-to-r from-emerald-900 via-teal-900 to-slate-900 rounded-xl p-6 md:p-8 text-white shadow-lg border border-emerald-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Building2 size={200} />
        </div>

        <div className="relative z-10 space-y-3">
          <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 text-[10px] font-black uppercase tracking-widest rounded-md border border-emerald-500/30 inline-flex items-center gap-1.5">
            <Store size={14} /> Módulo SaaS de Cashback - Painel da Empresa
          </span>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight">
            Ofereça Cashback na Sua Empresa
          </h1>
          <p className="text-xs md:text-sm text-emerald-100/80 max-w-2xl leading-relaxed">
            Cadastre seu estabelecimento para ter seu próprio painel de gestão de cashback, validar cupons de clientes, criar campanhas promocionais e aumentar suas vendas recorrentes.
          </p>
        </div>
      </div>

      {/* STATUS BANNER IF REQUESTED */}
      {req ? (
        <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm space-y-6 text-center">
          {req.status === 'pending' && (
            <div className="space-y-4">
              <div className="w-20 h-20 bg-amber-50 text-amber-500 rounded-xl flex items-center justify-center mx-auto shadow-inner animate-pulse">
                <Clock size={40} />
              </div>
              <div className="space-y-2">
                <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-black uppercase tracking-wider rounded-md inline-block">
                  Solicitação em Análise pelo Super Admin
                </span>
                <h3 className="text-xl font-black text-slate-800">
                  Sua solicitação de Painel da Empresa está sendo analisada!
                </h3>
                <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                  O Super Admin do sistema <strong className="text-slate-700">Automatiza e Vende</strong> recebeu o seu pedido para a empresa <strong className="text-slate-800">{req.company_name || companyName || 'seu negócio'}</strong>. Assim que for liberada, o menu do Painel da Empresa será ativado automaticamente.
                </p>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200/80 text-left text-xs space-y-2 max-w-md mx-auto">
                <div className="flex justify-between border-b border-slate-200 pb-2">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Empresa:</span>
                  <span className="font-bold text-slate-800">{req.company_name || companyName}</span>
                </div>
                <div className="flex justify-between border-b border-slate-200 pb-2">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Categoria:</span>
                  <span className="font-bold text-slate-800">{req.company_category || companyCategory}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Solicitante:</span>
                  <span className="font-bold text-slate-800">{currentUser?.name || req.user_name || 'Usuário'}</span>
                </div>
              </div>
            </div>
          )}

          {req.status === 'rejected' && (
            <div className="space-y-4">
              <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-xl flex items-center justify-center mx-auto shadow-inner">
                <XCircle size={40} />
              </div>
              <div className="space-y-2">
                <span className="px-3 py-1 bg-rose-100 text-rose-800 text-xs font-black uppercase tracking-wider rounded-md inline-block">
                  Solicitação Recusada
                </span>
                <h3 className="text-xl font-black text-slate-800">
                  Sua solicitação não pôde ser aprovada no momento
                </h3>
                <p className="text-xs text-slate-500 max-w-md mx-auto">
                  Por favor, verifique se os dados da sua empresa foram informados corretamente ou entre em contato com o suporte do Automatiza e Vende.
                </p>
              </div>

              <button
                onClick={() => setIsSubmitted(false)}
                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider rounded-lg transition-all shadow-md"
              >
                Enviar Nova Solicitação
              </button>
            </div>
          )}
        </div>
      ) : (
        /* REQUEST FORM */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl p-6 md:p-8 border border-slate-200/80 shadow-sm space-y-6">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
                <Building2 size={20} />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-800">Formulário de Solicitação</h2>
                <p className="text-xs text-slate-400">Preencha os dados do seu negócio para requerer o acesso</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                  Nome Fantasia da Empresa *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Padaria Bella Vista, Restaurante Gostoso"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:bg-white focus:border-emerald-500 outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                    Categoria do Negócio *
                  </label>
                  <select
                    value={companyCategory}
                    onChange={(e) => setCompanyCategory(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:bg-white focus:border-emerald-500 outline-none transition-all"
                  >
                    <option value="Alimentação & Gastronomia">Alimentação & Gastronomia</option>
                    <option value="Supermercado & Conveniência">Supermercado & Conveniência</option>
                    <option value="Posto de Combustível">Posto de Combustível</option>
                    <option value="Vestuário & Moda">Vestuário & Moda</option>
                    <option value="Academias & Fitness">Academias & Fitness</option>
                    <option value="Farmácias & Saúde">Farmácias & Saúde</option>
                    <option value="Beleza & Estética">Beleza & Estética</option>
                    <option value="Serviços Automotivos">Serviços Automotivos</option>
                    <option value="Pet Shop & Veterinária">Pet Shop & Veterinária</option>
                    <option value="Outros Serviços">Outros Serviços</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                    Cidade / Estado *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Ribeirão Preto - SP"
                    value={companyCity}
                    onChange={(e) => setCompanyCity(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:bg-white focus:border-emerald-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                    WhatsApp / Telefone para Contato *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="(16) 99999-8888"
                    value={userPhone}
                    onChange={(e) => setUserPhone(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:bg-white focus:border-emerald-500 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                    CNPJ ou CPF do Responsável
                  </label>
                  <input
                    type="text"
                    placeholder="00.000.000/0001-00"
                    value={cnpjCpf}
                    onChange={(e) => setCnpjCpf(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:bg-white focus:border-emerald-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                  Observações / Por que deseja oferecer cashback?
                </label>
                <textarea
                  rows={3}
                  placeholder="Conte-nos brevemente sobre sua loja ou expectativa..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:bg-white focus:border-emerald-500 outline-none transition-all resize-none"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider rounded-lg shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
              >
                <Send size={16} /> Solicitar Acesso de Empresa ao Super Admin
              </button>
            </form>
          </div>

          {/* BENEFIT HIGHLIGHTS */}
          <div className="space-y-4">
            <div className="bg-emerald-950 text-white rounded-xl p-6 border border-emerald-900 space-y-4">
              <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block">
                Recursos Incluídos no Painel
              </span>
              <ul className="space-y-3 text-xs text-slate-300">
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span>Configuração de porcentagem de cashback (Ex: 10% em todas compras)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span>Aprovação rápida de cupons e fotos de notas enviadas por clientes</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span>Criação de ofertas e campanhas promocionais turbinadas</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span>Participação na rede de Cashback Cruzado do ecossistema</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 rounded-xl p-5 border border-amber-200 space-y-2 text-xs">
              <div className="flex items-center gap-2 text-amber-800 font-bold">
                <ShieldCheck size={18} className="text-amber-600" /> Aprovação do Super Admin
              </div>
              <p className="text-amber-700 text-[11px]">
                Após o envio, o proprietário do sistema (Super Admin do Automatiza e Vende) analisará sua empresa no painel de aprovações e liberará suas funcionalidades de Lojista.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
